import { OrderStatus } from 'src/types';
import { OrderStatusFilterOption } from 'src/types/status_filter';
import {
  capitalizeFirstLetter,
  isClinicAdmin,
  isCustomerSuccessOrSalesUser,
  isLabTech,
  isOrderingPhysician,
} from 'src/utils';

export enum LdtStatusFilterValue {
  ALL_ORDERS = 'all_orders',
  REPORT_READY = 'report_ready',
  REPORT_FAX_PENDING = 'report_fax_pending',
  REPORT_FAXED = 'report_faxed',
  PROCESSING = 'processing',
  QC_APPROVED = 'qc_approved',
  QC_PENDING = 'qc_pending',
  DRAFTED = 'drafted',
  PATHOLOGY_REPORT_PENDING = 'pathology_report_pending',
  CLIN_VARS_PENDING_CS = 'clin_vars_pending_cs',
  CLIN_VARS_PENDING_TEMPUS = 'clin_vars_pending_tempus',
  IMAGE_PENDING = 'image_pending',
  IMAGE_QC_FAILED = 'image_qc_failed',
  AI_PROCESSING = 'ai_processing',
  REPORT_PENDING = 'report_pending',
  AI_ERROR = 'ai_error',
}

/**
 * Gets the display name for a default workflow order status based on user type.
 */
export function getDefaultWorkflowOrderStatusName(status: OrderStatus): string {
  const isCSOrSalesUser = isCustomerSuccessOrSalesUser();
  const isPhysicianOrClinicAdminUser = isOrderingPhysician() || isClinicAdmin();
  const isLabTechUser = isLabTech();

  if (isCSOrSalesUser) {
    return getDefaultWorkflowCSOrSalesUserOrderStatusName(status);
  } else if (isPhysicianOrClinicAdminUser) {
    return getDefaultWorkflowPhysicianOrClinicAdminUserOrderStatusName(status);
  } else if (isLabTechUser) {
    return getDefaultWorkflowLabTechUserOrderStatusName(status);
  }

  return capitalizeFirstLetter(status);
}

/**
 * Gets the display name for a default workflow CS or sales user order status.
 */
function getDefaultWorkflowCSOrSalesUserOrderStatusName(
  status: OrderStatus,
): string {
  switch (status) {
    case OrderStatus.Draft:
      return 'Draft';
    case OrderStatus.QualityControlPending:
      return 'QC Pending';
    case OrderStatus.QualityControlApproved:
      return 'QC Approved';
    case OrderStatus.PathologyFaxPending:
      return 'Pathology Fax Pending';
    case OrderStatus.PathologyFaxFailed:
      return 'Pathology Fax Failed';
    case OrderStatus.LabProcessing:
      return 'Lab Processing';
    case OrderStatus.LabError:
      return 'Lab Error';
    case OrderStatus.SlideImageProcessing:
      return 'Image Processing';
    case OrderStatus.SlideImageQualityControlPending:
      return 'Slide Image QC Pending';
    case OrderStatus.SlideImageQualityControlFailed:
      return 'Slide Image QC Failed';
    case OrderStatus.AiProcessing:
      return 'AI Processing';
    case OrderStatus.AiError:
      return 'AI Error';
    case OrderStatus.ReportPending:
      return 'Report Pending';
    case OrderStatus.ReportRejected:
      return 'Report Rejected';
    case OrderStatus.ReportReady:
      return 'Report Ready';
    case OrderStatus.Canceled:
      return 'Canceled';
    case OrderStatus.ReportFaxPending:
      return 'Report Fax Pending';
    case OrderStatus.ReportFaxFailed:
      return 'Report Fax Failed';
    case OrderStatus.ReportFaxed:
      return 'Report Faxed';
    case OrderStatus.Deleted:
      return 'Deleted';
    default:
      return capitalizeFirstLetter(status);
  }
}

const PROCESSING_ORDER_STATUS_DISPLAY_NAME = 'Processing';

/**
 * Gets the display name for a default workflow physician or clinic admin user order status.
 */
function getDefaultWorkflowPhysicianOrClinicAdminUserOrderStatusName(
  status: OrderStatus,
): string {
  switch (status) {
    case OrderStatus.Draft:
      return 'Draft';
    case OrderStatus.QualityControlPending:
      return 'Order Created';
    case OrderStatus.QualityControlApproved:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.PathologyFaxPending:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.PathologyFaxFailed:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.LabProcessing:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.LabError:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.SlideImageProcessing:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.SlideImageQualityControlPending:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.SlideImageQualityControlFailed:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.AiProcessing:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.AiError:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportPending:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportRejected:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportReady:
      return 'Report Released';
    case OrderStatus.Canceled:
      return 'Canceled';
    case OrderStatus.ReportFaxPending:
      return 'Report Fax Pending';
    case OrderStatus.ReportFaxFailed:
      return 'Report Fax Pending';
    case OrderStatus.ReportFaxed:
      return 'Report Faxed';
    case OrderStatus.Deleted:
      return 'Deleted';
    default:
      return capitalizeFirstLetter(status);
  }
}

/**
 * Gets the display name for a default workflow lab tech user order status.
 */
function getDefaultWorkflowLabTechUserOrderStatusName(
  status: OrderStatus,
): string {
  switch (status) {
    case OrderStatus.Draft:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.LabProcessing:
      return 'Slide Image Needed';
    case OrderStatus.SlideImageProcessing:
      return 'Slide Image Processing';
    case OrderStatus.SlideImageQualityControlFailed:
      return 'Image QC Failed';
    case OrderStatus.AiError:
      return 'Slide Image Needed';
    case OrderStatus.QualityControlPending:
      return 'QC Pending';
    case OrderStatus.QualityControlApproved:
      return 'QC Approved';
    case OrderStatus.PathologyFaxPending:
      return 'Pathology Fax Pending';
    case OrderStatus.PathologyFaxFailed:
      return 'Pathology Fax Failed';
    case OrderStatus.LabError:
      return 'Lab Error';
    case OrderStatus.SlideImageQualityControlPending:
      return 'Slide Image QC Pending';
    case OrderStatus.AiProcessing:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportPending:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportRejected:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportReady:
      return 'Report Released';
    case OrderStatus.Canceled:
      return 'Canceled';
    case OrderStatus.ReportFaxPending:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportFaxFailed:
      return PROCESSING_ORDER_STATUS_DISPLAY_NAME;
    case OrderStatus.ReportFaxed:
      return 'Report Released';
    case OrderStatus.Deleted:
      return 'Deleted';
    default:
      return capitalizeFirstLetter(status);
  }
}

export function createOption(
  value: LdtStatusFilterValue,
  label: string,
  statuses?: OrderStatus[],
): [string, OrderStatusFilterOption] {
  return [
    value,
    {
      label,
      value,
      ...(statuses && { statuses }),
    },
  ];
}
